import React from 'react';
import {isEmpty as _isEmpty, isNil as _isNil} from 'lodash';
import {Dropdown, Icon, Popup} from 'semantic-ui-react';
import PropTypes from 'prop-types';

import DefaultForm from '@appComponents/DefaultForm';
import Chip from '@ui/Chip';
import {dropdownOptionParser, dropdownOptionParserWithTooltip} from '@utils/dropdownOptionParser';
import {convertMinutesWithOneDecimalToSeconds, convertToInt, getMinutesAndSeconds} from '@utils/helpers';
import {
    EVENT_CONTENT_VARIANT_STANDARD_FEED,
    INCIDENT_DURATION_UNIT_MINUTE,
    INCIDENT_DURATION_UNIT_PERCENT,
    INCIDENT_REPORT_TYPE,
    SECURITY_PRIVILEGES_UPDATE,
} from '@constants/variables';
import {getLink} from '@appComponents/Link';
import {GetScopeTreeOptions, GetReports} from '@graphql/reports/query';
import {showErrorModal} from '@utils/modal';
import {refetchQueryByName} from '@utils/apollo';
import {hasPrivileges} from '@appComponents/Authorization';
import {REPORT} from '@constants/resources';
import {getTimeDifferenceInMinutes, getTimeDifferenceInMinutesWithOneDecimal} from '@utils/momentWrapper';
import {DURATION_BASED_ON_SPORTRADAR_DATA, DURATION_WAS_SET_MANUALLY} from '@constants/messages';

export const hasUpdatePrivileges = () => hasPrivileges({
    [REPORT]: SECURITY_PRIVILEGES_UPDATE,
});

export class EventReportIncidentForm extends DefaultForm {
    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this.setDeleteSuccessCallback(() => {
            refetchQueryByName('ReportState');
        });
        this.getScopeTreeOptions();
        this.checkOtherNonIncidentReportExists();
    }

    checkOtherNonIncidentReportExists = () => {
        this.runApolloRequest('query', {
            query: GetReports,
            fetchPolicy: 'no-cache',
            variables: {
                event: this.props.formParams.event,
            },
        }).then((result) => {
            const editingReportId = convertToInt(this.props.formParams.id);
            const otherNonIncidentReportExists = (result.data?.reports ?? []).some(
                (report) => convertToInt(report.report_type?.id) !== INCIDENT_REPORT_TYPE
                    && convertToInt(report.id) !== editingReportId
            );
            this.setState(() => ({
                otherNonIncidentReportExists,
            }));
        }).catch((error) => {
            showErrorModal({
                header: 'Failed to fetch',
                text: `Could not fetch information about non-incident report existence (error: ${error})`,
            });
        });
    };

    getScopeTreeOptions = (isIncident = true) => {
        this.runApolloRequest('query', {
            query: GetScopeTreeOptions,
            fetchPolicy: 'no-cache',
            variables: {
                event: this.props.formParams.event,
                report: this.props.formParams?.id,
                isIncident: isIncident,
            },
        }).then((result) => {
            this.setState(() => ({
                scopeTreeOptions: result.data.reportScopes,
            }));
        }).catch((error) => {
            showErrorModal({
                header: 'Failed to fetch',
                text: `Could not fetch scope tree options (error: ${error})`,
            });
        });
    };

    handleIncidentDurationUnitChange = (e, {value}) => {
        this.setState({incident_duration_unit: value});
    };

    mapIncidentDurationUnits = (units) => {
        return units?.map(unit => ({
            key: unit.short_name,
            text: unit.short_name,
            value: unit.id,
        }));
    };

    setIncidentDurationField = (incidentDurationUnits, entireDuration, initialValue) => {
        let durationUnitMessage = null;

        if (initialValue === INCIDENT_DURATION_UNIT_MINUTE) {
            if (this.props.formParams.duration) {
                const durationTime = getMinutesAndSeconds(this.props.formParams.duration);
                durationUnitMessage = DURATION_WAS_SET_MANUALLY(this.props.formParams.duration, durationTime);
            } else if (!_isNil(this.props.formParams.matchStarted) && !_isNil(this.props.formParams.matchEnded)) {
                const durationInMinutesWithDecimal = getTimeDifferenceInMinutesWithOneDecimal(
                    this.props.formParams.matchEnded, this.props.formParams.matchStarted
                );
                const durationTime = getMinutesAndSeconds(getTimeDifferenceInMinutes(
                    this.props.formParams.matchEnded, this.props.formParams.matchStarted
                ));
                durationUnitMessage = DURATION_BASED_ON_SPORTRADAR_DATA(durationInMinutesWithDecimal, durationTime);
            } else {
                durationUnitMessage =
                    <>
                        <Icon name='warning' color='red' />
                        This event has no duration yet. A duration will be needed in order to proceed this incident properly.
                    </>;
            }
        }

        if (!entireDuration) {
            const getDurationUnitLabel = (initialValue) => {
                switch (initialValue) {
                    case INCIDENT_DURATION_UNIT_MINUTE:
                        return 'min';
                    case INCIDENT_DURATION_UNIT_PERCENT:
                        return '%';
                }
            };

            this.setField('incident_duration', {
                hidden: false,
                required: true,
                onlyDigitsAllowed: initialValue === INCIDENT_DURATION_UNIT_PERCENT,
                step: initialValue === INCIDENT_DURATION_UNIT_PERCENT ? '1' : '0.1',
                min: initialValue === INCIDENT_DURATION_UNIT_PERCENT ? 1 : 0.1,
                precision: initialValue === INCIDENT_DURATION_UNIT_PERCENT ? 0 : 1,
                labelDescription: {
                    content: (!hasUpdatePrivileges() && this.props.formParams.id)
                        ? <div className='muted disabled-label-description'>{getDurationUnitLabel(initialValue)}</div>
                        : <Dropdown
                            options={this.mapIncidentDurationUnits(incidentDurationUnits)}
                            defaultValue={initialValue}
                            onChange={this.handleIncidentDurationUnitChange}
                        />,
                },
                description: <div className='--paddingLeft-4-5-rem'>{durationUnitMessage}</div>,
            });
        }
    };

    createTreeOptions = (data = []) => {
        return data.map(eventContent => ({
            value: String(eventContent.id),
            label: EVENT_CONTENT_VARIANT_STANDARD_FEED !== eventContent.variant.id
                ? (<label>
                    {eventContent.text}{' '}
                    <Popup
                        trigger={
                            <span>
                                <Chip
                                    size='small'
                                    color='primary'
                                    className='--marginLeft-0-5-em contentVariantLabel'
                                    label={eventContent.variant.short_name}
                                />
                            </span>
                        }
                        content={eventContent.variant.name}
                        size='small'
                        hoverable
                    />
                </label>)
                : eventContent.text,
            name: eventContent.text,
            children: eventContent.distributions.map(distribution => ({
                value: String(distribution.id),
                label: distribution.name,
                name: distribution.name,
                children: distribution.stream_variants.map(variant => ({
                    value: String(variant.id),
                    label: variant.name,
                    name: variant.name,
                    selected: variant.selected,
                })),
            })),
        }));
    };

    updateOptionsFields = (incidentSources, incidentTypes, prevProps, formValues, prevState) => {
        if (!_isEmpty(incidentSources) && incidentSources !== prevProps.GraphQLOptionsData.incidentSources) {
            this.setField('incident_source', {
                options: dropdownOptionParser(incidentSources),
            });
        }

        if (!_isEmpty(incidentTypes) && incidentTypes !== prevProps.GraphQLOptionsData.incidentTypes) {
            this.setField('incident_type', {
                options: dropdownOptionParserWithTooltip(incidentTypes),
            });
        }

        if (_isEmpty(formValues.report_scopes) && prevState.scopeTreeOptions !== this.state.scopeTreeOptions) {
            this.setField('report_scopes', {options: this.createTreeOptions(this.state.scopeTreeOptions)});
        }
    };

    handleReportScopesVisibility = (formValues, prevProps) => {
        if (formValues.includes_all_streams !== prevProps.formValues.includes_all_streams) {
            if (!formValues.includes_all_streams) {
                this.setField('report_scopes', {
                    hidden: false,
                    required: true,
                });
            } else {
                this.setField('report_scopes', {
                    hidden: true,
                    required: false,
                });
            }
        }
    };

    componentDidUpdate(prevProps, prevState) {
        super.componentDidUpdate(prevProps);
        const {GraphQLOptionsData: {incidentDurationUnits, incidentSources, incidentTypes}, formValues} = this.props;

        this.updateOptionsFields(incidentSources, incidentTypes, prevProps, formValues, prevState);
        this.handleReportScopesVisibility(formValues, prevProps);

        if (this.state.incident_duration_unit !== prevState.incident_duration_unit) {
            this.setIncidentDurationField(incidentDurationUnits, formValues.entire_duration, this.state.incident_duration_unit);
            this.setField('incident_duration_unit', {
                defaultValue: this.state.incident_duration_unit,
                timestamp: Date.now(),
            });
        }

        if (this.state.otherNonIncidentReportExists
            && this.state.otherNonIncidentReportExists !== prevState.otherNonIncidentReportExists) {
            this.setField('includes_all_streams', {
                hidden: true,
                defaultValue: false,
            });
        }

        if (formValues.entire_duration !== prevProps.formValues.entire_duration) {
            const entireDurationHidden = formValues.entire_duration;
            this.setField('incident_duration', {
                hidden: entireDurationHidden,
                required: !entireDurationHidden,
            });
        }
    }

    transformScopeToReportScopes = (scope) => {
        const reportScopes = [];

        scope.forEach(item => {
            item.children.forEach(distribution => {
                distribution.children.forEach(variant => {
                    const existingScope = this.props.GraphQLEntityData?.report?.report_scopes?.find(
                        s => s.distribution.id === distribution.value && s.stream_variant.id === variant.value
                    );

                    reportScopes.push({
                        id: existingScope ? existingScope.id : null,
                        distribution: distribution.value,
                        stream_variant: variant.value,
                    });
                });
            });
        });

        return reportScopes;
    };

    prepareDataForSubmit(data) {
        const incidentDurationUnit = !isNaN(convertToInt(this.state.incident_duration_unit))
            ? convertToInt(this.state.incident_duration_unit)
            : convertToInt(data.incident_duration_unit);
        let incidentDuration;

        if (data.incident_duration && incidentDurationUnit === convertToInt(INCIDENT_DURATION_UNIT_PERCENT)) {
            incidentDuration = convertToInt(data.incident_duration);
        } else if (data.incident_duration && incidentDurationUnit === convertToInt(INCIDENT_DURATION_UNIT_MINUTE)) {
            incidentDuration = convertMinutesWithOneDecimalToSeconds(data.incident_duration);
        }

        return {
            ...this.props.Model.dataMap,
            ...data,
            comment: data.comment ?? '',
            incident_source: String(data.incident_source[0]),
            incident_type: String(data.incident_type[0]),
            incident_duration: data.entire_duration ? 100 : incidentDuration,
            incident_duration_unit: data.entire_duration
                ? convertToInt(INCIDENT_DURATION_UNIT_PERCENT)
                : incidentDurationUnit,
            report_scopes: data.includes_all_streams ? [] : this.transformScopeToReportScopes(data.report_scopes),
            event: this.props.formParams.event,
            report_type: INCIDENT_REPORT_TYPE,
        };
    }

    renderCancelButton = () => super.renderCancelButton({
        type: 'button',
        onClick: () => this.props.history.push(
            getLink('events.report.index', {id: this.props.formParams.event})
        ),
    });
}

EventReportIncidentForm.propTypes = {
    GraphQLEntityData: PropTypes.shape({
        report: PropTypes.shape({
            report_scopes: PropTypes.array,
            incident_type: PropTypes.object,
            incident_source: PropTypes.object,
        }),
    }),
    GraphQLOptionsData: PropTypes.shape({
        incidentSources: PropTypes.array,
        incidentTypes: PropTypes.array,
        incidentDurationUnits: PropTypes.array,
    }),
    formValues: PropTypes.shape({
        incident_source: PropTypes.array,
        incident_type: PropTypes.array,
        report_scopes: PropTypes.array,
        entire_duration: PropTypes.bool,
        incident_duration_unit: PropTypes.number,
    }),
    formParams: PropTypes.shape({
        id: PropTypes.number,
        event: PropTypes.number,
        reportType: PropTypes.string,
    }),
    Model: PropTypes.shape({
        fields: PropTypes.object,
        dataMap: PropTypes.object,
    }),
    history: PropTypes.shape({
        push: PropTypes.func,
    }),
};
